import { useMemo } from 'react';
import { useOrder } from '@olo-web/domain/orders/queries/useOrder';
import { calculateTipAmount } from '@olo-web/domain/orders/functions/calculateTips';
import { useCurrentOrderTips, useIsOrderReceiptPage } from '@olo-web/utils/common/hooks';
import { useIsSplitByAmount } from '@olo-web/utils/common/hooks/useIsSplitByAmount';
import { useIsOrderConfirmationPage } from '@olo-web/utils/common/hooks/useIsOrderConfirmationPage';
import { useLastGuestPayment } from '@olo-web/domain/orders/hooks/useLastGuestPayment';
import { sumPayments } from '@olo-web/domain/orders/functions/sumPayments';
import { useCustomPaymentAmount } from './useCustomPaymentAmount';

export const useTipAmount = (isGetFromFullAmount = false) => {
  const orderTips = useCurrentOrderTips();
  const { data: order } = useOrder();
  const isSplitByAmount = useIsSplitByAmount();
  const isOrderConfirmationPage = useIsOrderConfirmationPage();
  const isHistoricalOrderPage = useIsOrderReceiptPage();
  const lastGuestPayment = useLastGuestPayment();
  const { total: customTotal } = useCustomPaymentAmount();

  let ticketTotal = 0;
  if (customTotal != null && !isGetFromFullAmount) {
    ticketTotal = customTotal;
  } else if (!isGetFromFullAmount && order?.payments?.length) {
    if (order?.balanceDueAmount != null && order.balanceDueAmount !== '') {
      ticketTotal = +order.balanceDueAmount;
    } else {
      const displayTotal = order?.displayTotalAmount != null ? +order.displayTotalAmount : 0;
      ticketTotal = Math.max(0, displayTotal - sumPayments(order?.payments ?? []));
    }
  } else {
    ticketTotal = +order?.preDiscountSubtotal;
  }

  return useMemo(() => {
    if (isOrderConfirmationPage || isHistoricalOrderPage) {
      if (isSplitByAmount) {
        return lastGuestPayment?.tipAmount;
      }

      return (
        order?.payments
          ?.reduce((tipSum, currentPayment) => tipSum + +currentPayment?.tipAmount, 0)
          .toFixed(2) || '0'
      );
    }

    return calculateTipAmount(ticketTotal, orderTips).toFixed(2);
  }, [
    orderTips,
    ticketTotal,
    isOrderConfirmationPage,
    isHistoricalOrderPage,
    order,
    isSplitByAmount,
    lastGuestPayment,
  ]);
};
